import React from 'react';
import { ArrowRight, BarChart3, CheckCircle, Mail, ShieldCheck, Sparkles, Target } from 'lucide-react';
import { motion } from 'framer-motion';

const calculatorUrl = 'https://calc.trilogyoneconsulting.com/';

const calculatorInputs = [
  'organization type',
  'team size',
  'revenue range',
  'biggest time leak',
  'rough weekly manual-work hours',
];

const resultCards = [
  {
    icon: BarChart3,
    title: 'Value range',
    body: 'A directional estimate of recoverable time and money. Not a guarantee.',
  },
  {
    icon: Target,
    title: 'Likely first workflow',
    body: 'A practical starting point for reducing drag without over-automating.',
  },
  {
    icon: ShieldCheck,
    title: 'Trust-first next step',
    body: 'A bridge into the paid audit when you want the safer, deeper recommendation.',
  },
];

function BeforeAiTouchesClientWorkPage() {
  return (
    <main className="min-h-screen bg-white text-[#1c334a]">
      <section className="relative overflow-hidden bg-[#1c334a] py-16 text-white sm:py-24">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(4,154,170,0.35),transparent_35%),radial-gradient(circle_at_bottom_left,rgba(216,224,5,0.2),transparent_30%)]" />
        <div className="container relative z-10 mx-auto grid max-w-screen-xl gap-10 px-5 sm:px-8 lg:grid-cols-[0.95fr_1.05fr] lg:items-center">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} className="space-y-7">
            <span className="inline-flex rounded-full bg-[#d8e005] px-4 py-2 text-xs font-black uppercase tracking-[0.22em] text-[#1c334a]">60-second estimate</span>
            <h1 className="text-4xl font-black leading-tight sm:text-6xl">Find your hidden AI opportunity without starting with a long audit.</h1>
            <p className="max-w-2xl text-lg leading-8 text-white/85">
              Get a quick, directional estimate of the time and money your firm may be able to recover with one safe, supervised AI workflow.
            </p>
            <div className="flex flex-col gap-4 sm:flex-row">
              <a href={calculatorUrl} className="inline-flex items-center justify-center rounded-2xl bg-[#d8e005] px-7 py-4 font-black text-[#1c334a] shadow-xl transition hover:-translate-y-0.5 hover:bg-white">
                Estimate my AI opportunity <ArrowRight className="ml-2 h-5 w-5" />
              </a>
              <a href="/audit/checkout" className="inline-flex items-center justify-center rounded-2xl border-2 border-white/70 px-7 py-4 font-black text-white transition hover:bg-white hover:text-[#1c334a]">
                Take the $9 audit
              </a>
            </div>
          </motion.div>

          <div className="rounded-[2rem] border border-white/15 bg-white/10 p-6 shadow-2xl backdrop-blur sm:p-8">
            <div className="rounded-3xl bg-white p-6 text-[#1c334a] shadow-xl">
              <Sparkles className="h-12 w-12 text-[#049aaa]" />
              <h2 className="mt-5 text-2xl font-black">What the calculator should feel like</h2>
              <p className="mt-3 leading-7 text-slate-700">
                Quick input, useful estimate, then a clear bridge into the deeper audit only if they want the specific workflow and guardrails.
              </p>
              <div className="mt-6 grid gap-3">
                {calculatorInputs.map((input) => (
                  <div key={input} className="flex items-center gap-3 rounded-2xl bg-[#f7fbff] p-4 font-bold text-[#1c334a]">
                    <CheckCircle className="h-5 w-5 shrink-0 text-[#049aaa]" />
                    {input}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-24">
        <div className="container mx-auto max-w-screen-xl px-5 sm:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <span className="inline-flex rounded-full bg-[#049aaa]/10 px-4 py-2 text-xs font-black uppercase tracking-[0.22em] text-[#049aaa]">The new funnel logic</span>
            <h2 className="mt-6 text-3xl font-black leading-tight sm:text-5xl">The calculator estimates. The audit decides what to do first.</h2>
            <p className="mt-5 text-lg leading-8 text-slate-700">
              The calculator should not become another audit. It creates the wow moment. The paid audit turns that rough number into your first safe AI workflow, what not to automate yet, and a 14-day proof plan.
            </p>
          </div>

          <div className="mt-12 grid gap-5 md:grid-cols-3">
            {resultCards.map(({ icon: Icon, title, body }) => (
              <article key={title} className="rounded-[1.75rem] border border-[#049aaa]/10 bg-white p-6 shadow-sm">
                <Icon className="h-10 w-10 text-[#049aaa]" />
                <h3 className="mt-5 text-xl font-black">{title}</h3>
                <p className="mt-3 leading-7 text-slate-700">{body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#f7fbff] py-16 sm:py-24">
        <div className="container mx-auto max-w-screen-xl px-5 sm:px-8">
          <div className="grid gap-8 rounded-[2rem] bg-[#1c334a] p-8 text-white shadow-2xl lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
            <div>
              <Mail className="h-12 w-12 text-[#d8e005]" />
              <h2 className="mt-5 text-3xl font-black leading-tight">Do not block the result behind email.</h2>
              <p className="mt-4 text-lg leading-8 text-white/85">
                Show the estimate first. Then ask, "Want a copy of this estimate?" That builds more trust than forcing an opt-in before giving value.
              </p>
            </div>
            <div className="rounded-3xl bg-white p-6 text-[#1c334a]">
              <p className="text-sm font-black uppercase tracking-[0.18em] text-[#049aaa]">Bridge copy</p>
              <p className="mt-4 text-xl font-black leading-8">
                Want the specific workflow we would start with?
              </p>
              <p className="mt-3 leading-7 text-slate-700">
                The calculator gives a rough estimate. The $9 AI Opportunity Audit turns that estimate into a safer recommendation, including what not to automate yet and how to prove value in 14 days.
              </p>
              <a href="/audit/checkout" className="mt-6 inline-flex items-center justify-center rounded-2xl bg-[#d8e005] px-6 py-4 font-black text-[#1c334a] transition hover:bg-[#049aaa] hover:text-white">
                Continue to the audit <ArrowRight className="ml-2 h-5 w-5" />
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

export default BeforeAiTouchesClientWorkPage;
